import type { Directive } from 'vue'

type DigitsOnlyInput = HTMLInputElement & {
  __digitsOnlyHandlers?: {
    beforeInput: (event: InputEvent) => void
    input: () => void
    paste: (event: ClipboardEvent) => void
    drop: (event: DragEvent) => void
    compositionStart: () => void
    compositionEnd: () => void
  }
  __digitsOnlyComposing?: boolean
}

export const digitsOnly: Directive<HTMLInputElement> = {
  mounted(element) {
    const input = element as DigitsOnlyInput

    const handlers = {
      beforeInput(event: InputEvent) {
        if (input.__digitsOnlyComposing || event.isComposing || event.data == null) return
        if (event.data === toDigits(event.data)) return

        event.preventDefault()
        insertDigits(input, event.data, event.inputType)
      },
      input() {
        if (!input.__digitsOnlyComposing) {
          sanitizeInputValue(input)
        }
      },
      paste(event: ClipboardEvent) {
        const text = event.clipboardData?.getData('text') ?? ''
        if (text === '') return

        event.preventDefault()
        insertDigits(input, text, 'insertFromPaste')
      },
      drop(event: DragEvent) {
        const text = event.dataTransfer?.getData('text') ?? ''
        if (text === '') return

        event.preventDefault()
        input.focus()
        insertDigits(input, text, 'insertFromDrop')
      },
      compositionStart() {
        input.__digitsOnlyComposing = true
      },
      compositionEnd() {
        input.__digitsOnlyComposing = false
        sanitizeInputValue(input)
      },
    }

    input.__digitsOnlyHandlers = handlers
    input.addEventListener('beforeinput', handlers.beforeInput)
    input.addEventListener('input', handlers.input)
    input.addEventListener('paste', handlers.paste)
    input.addEventListener('drop', handlers.drop)
    input.addEventListener('compositionstart', handlers.compositionStart)
    input.addEventListener('compositionend', handlers.compositionEnd)
  },
  beforeUnmount(element) {
    const input = element as DigitsOnlyInput
    const handlers = input.__digitsOnlyHandlers
    if (!handlers) return

    input.removeEventListener('beforeinput', handlers.beforeInput)
    input.removeEventListener('input', handlers.input)
    input.removeEventListener('paste', handlers.paste)
    input.removeEventListener('drop', handlers.drop)
    input.removeEventListener('compositionstart', handlers.compositionStart)
    input.removeEventListener('compositionend', handlers.compositionEnd)
    delete input.__digitsOnlyHandlers
    delete input.__digitsOnlyComposing
  },
}

function toDigits(value: string): string {
  return value.replace(/\D/g, '')
}

function insertDigits(input: HTMLInputElement, value: string, inputType: string): void {
  const digits = limitInsertedText(input, toDigits(value))
  if (digits === '') return

  const start = input.selectionStart ?? input.value.length
  const end = input.selectionEnd ?? input.value.length
  input.setRangeText(digits, start, end, 'end')
  dispatchInput(input, inputType)
}

function limitInsertedText(input: HTMLInputElement, value: string): string {
  if (input.maxLength < 0) return value

  const start = input.selectionStart ?? input.value.length
  const end = input.selectionEnd ?? input.value.length
  const selectedLength = Math.max(0, end - start)
  const availableLength = input.maxLength - (input.value.length - selectedLength)
  return value.slice(0, Math.max(0, availableLength))
}

function sanitizeInputValue(input: HTMLInputElement): void {
  const sanitized = toDigits(input.value)
  if (input.value === sanitized) return

  const selectionStart = input.selectionStart ?? sanitized.length
  const removedBeforeSelection = input.value.slice(0, selectionStart).length - toDigits(input.value.slice(0, selectionStart)).length
  input.value = sanitized
  input.setSelectionRange(
    Math.max(0, selectionStart - removedBeforeSelection),
    Math.max(0, selectionStart - removedBeforeSelection),
  )
  dispatchInput(input, 'insertReplacementText')
}

function dispatchInput(input: HTMLInputElement, inputType: string): void {
  input.dispatchEvent(new InputEvent('input', {
    bubbles: true,
    inputType,
  }))
}
